/**
 * RFQ (Request for Quote) — Çoklu tedarikçi teklif sistemi (Sprint 34).
 *
 * Kullanıcı: "10 kişiden fiyat alıcaklar, 1 kişiden ürünü almış olacaklar."
 *
 * 3 görünüm:
 * 1. LIST: tüm RFQ'lar (status, supplier count, total)
 * 2. CREATE WIZARD: ihtiyaç listesi + tedarikçi seçimi → RFQ aç
 * 3. DETAIL: tek RFQ — teklifleri karşılaştır, fiyat gir, kabul et
 */
import {
  ActionIcon, Alert, Badge, Button, Card, Checkbox, Divider, Group, Loader, Modal,
  NumberInput, Paper, Pill, ScrollArea, SegmentedControl, Select, Stack, Switch,
  Table, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle, IconArrowLeft, IconArrowsSplit, IconBrandWhatsapp, IconBulb,
  IconCategory2, IconCheck, IconFileDownload, IconPlus, IconSearch, IconSend,
  IconStar, IconTrash, IconTrophy, IconX,
} from "@tabler/icons-react";
import { apiClient } from "../../../shared/api";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useState } from "react";
import {
  useAcceptQuote, useAcceptSplit, useCancelRFQ, useCategoryItems, useCoverableLines,
  useCreateRFQ, useInfiniteResource, useRecordQuoteResponse, useReorderSuggestions,
  useResource, useRFQ, useRFQComparison, useSendRFQ, useSuggestSuppliers,
  type SupplierSuggestion,
} from "../../../shared/api";
import {
  MoneyDisplay, PageHeader, confirmAction, InfiniteTable, EmptyState, formatDateTime,
} from "../../../shared/ui";
import { IngredientPickerModal } from "../components/IngredientPickerModal";
import { SupplierPickerModal } from "../components/SupplierPickerModal";
import { SupplyItemPickerModal } from "../components/SupplyItemPickerModal";
import { PurchaseOrderDetailDrawer } from "./PurchaseOrders";

// Kural: SON adım (Tamamlandı) = yeşil, bir önceki (Kabul Edildi) = mavi.
const STATUS_COLOR: Record<string, string> = {
  DRAFT: "gray",
  SENT: "grape",          // gönderildi (erken adım)
  QUOTES_RECEIVED: "violet",
  ACCEPTED: "blue",       // kabul edildi (PO açıldı) = bir önceki adım → mavi
  COMPLETED: "green",     // tüm PO'lar faturalandı → SON adım (döngü bitti) → yeşil
  CANCELLED: "red",
  EXPIRED: "gray",
};
const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Taslak",
  SENT: "Gönderildi",
  QUOTES_RECEIVED: "Teklifler Geldi",
  ACCEPTED: "PO Onayı",
  COMPLETED: "Tamamlandı",
  CANCELLED: "İptal",
  EXPIRED: "Süresi Geçti",
};

// RFQ satırı ingredient XOR supply_item taşır — gösterimde ikisine de fallback.
const lineName = (l: any): string => l?.ingredient_name || l?.supply_item_name || "—";
const lineUnit = (l: any): string => l?.unit || l?.ingredient_unit || l?.supply_item_unit || "";
// Teklif eşlemesinde satır kimliği (ingredient veya supply_item FK).
const lineItemId = (l: any): string => l?.ingredient || l?.supply_item || "";

// RFQ detay açılışını URL hash'ine yansıt: #rfq → liste, #rfq/<id> → detay.
// AppShell hash'in İLK segmentini ("rfq") okur, alt segmenti yok sayar → çakışmaz.
// Böylece tarayıcı "Geri" tuşu detaydan LİSTEYE döner (eski hata: detay history'ye
// hiç yazılmadığı için geri tuşu RFQ'dan önceki modüle atlıyordu).
function rfqIdFromHash(): string | null {
  const m = window.location.hash.match(/^#\/?rfq\/([^/?#]+)/i);
  return m ? decodeURIComponent(m[1]) : null;
}

export function RFQPage() {
  const [view, setView] = useState<"list" | "create" | "detail">(
    () => (rfqIdFromHash() ? "detail" : "list"),
  );
  const [selectedId, setSelectedId] = useState<string | null>(() => rfqIdFromHash());

  // Geri/İleri (popstate) → hash'ten view'i senkronla. Hash'te id varsa detay,
  // yoksa liste. CreateWizard hash kullanmaz (kısa ömürlü, geri = liste).
  useEffect(() => {
    const onPop = () => {
      const id = rfqIdFromHash();
      if (id) { setSelectedId(id); setView("detail"); }
      else { setSelectedId(null); setView((v) => (v === "create" ? "create" : "list")); }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  function openDetail(id: string) {
    setSelectedId(id);
    setView("detail");
    // History'ye #rfq/<id> ekle → geri tuşu önce listeye (#rfq) döner.
    window.history.pushState(null, "", `#rfq/${encodeURIComponent(id)}`);
  }
  function backToList() {
    setView("list");
    setSelectedId(null);
    // Hash'te detay id'si varsa geçmişten düş (tarayıcı geri tuşuyla tutarlı).
    if (rfqIdFromHash()) window.history.back();
    else window.history.replaceState(null, "", "#rfq");
  }

  if (view === "create") {
    return <CreateRFQWizard onDone={() => setView("list")} onCancel={() => setView("list")} />;
  }
  if (view === "detail" && selectedId) {
    return <RFQDetail rfqId={selectedId} onBack={backToList} />;
  }
  return (
    <RFQList
      onCreate={() => setView("create")}
      onSelect={openDetail}
    />
  );
}

// ============================================================
// LIST
// ============================================================
function RFQList({
  onCreate, onSelect,
}: { onCreate: () => void; onSelect: (id: string) => void }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  // GR/PO listeleriyle aynı desen: sonsuz kaydırma + arama + durum filtresi.
  // 100+ RFQ'da liste tek seferde çekilmez; backend hafif serializer
  // (line_count/category_names) döndürür, detay (useRFQ) full veriyi ayrı çeker.
  // Durum filtresi backend filterset_fields=["status"] üzerinden.
  const {
    items: rfqs, total, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<any>("rfqs", {
    ...(search.trim() ? { search: search.trim() } : {}),
    // Durum filtresi backend query param'ı → opts.filters içinde olmalı (kök
    // seviyede verilince useInfiniteResource yok sayıyordu = filtre çalışmıyordu).
    ...(statusFilter ? { filters: { status: statusFilter } } : {}),
  });

  return (
    <Stack gap="md">
      <PageHeader
        title="🛒 Tedarik Teklifleri (RFQ)"
        subtitle='"10 tedarikçiden fiyat al, en uygunundan al" — çoklu tedarikçi teklif sistemi'
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={onCreate}>
            Yeni RFQ
          </Button>
        }
      />
      <Group>
        <TextInput
          placeholder="RFQ no ile ara..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 360 }}
        />
        <Select
          placeholder="Tüm durumlar"
          clearable
          value={statusFilter}
          onChange={setStatusFilter}
          data={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
          style={{ width: 200 }}
        />
        <Text size="sm" c="dimmed">{total} teklif</Text>
      </Group>
      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={rfqs}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onRowClick={(r) => onSelect(r.id)}
          emptyContent={<EmptyState title="Henüz RFQ yok — 'Yeni RFQ' ile başlayın" />}
          columns={[
            { header: "RFQ No", render: (r) => <span style={{ fontWeight: 600 }}>{r.rfq_number}</span> },
            {
              header: "Durum",
              render: (r) => <Badge color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status] || r.status}</Badge>,
            },
            {
              header: "Kategoriler",
              render: (r) => {
                const cats: string[] = r.category_names || [];
                if (cats.length === 0) return <Text c="dimmed" size="sm">—</Text>;
                // Tek kategori → adını göster. Birden fazla → "N kategori" rozeti;
                // üstüne gelince (hover) hepsi listelenir (satır şişmesin / çok
                // kategorili RFQ'da düzen bozulmasın).
                if (cats.length === 1) {
                  return <Badge size="sm" variant="light" color="grape">{cats[0]}</Badge>;
                }
                return (
                  <Tooltip label={cats.join(", ")} withArrow multiline>
                    <Badge
                      size="sm"
                      variant="light"
                      color="grape"
                      style={{ cursor: "help" }}
                    >
                      {cats.length} kategori
                    </Badge>
                  </Tooltip>
                );
              },
            },
            { header: "Ürün", align: "right", render: (r) => r.line_count ?? 0 },
            { header: "Tedarikçi", align: "right", render: (r) => r.response_count ?? 0 },
            { header: "Tarih", render: (r) => formatDateTime(r.created_at) },
            { header: "", align: "right", render: () => <Button variant="subtle" size="xs">Detay</Button> },
          ]}
        />
      </Card>
    </Stack>
  );
}

// ============================================================
// CREATE WIZARD
// ============================================================
type LineKind = "INGREDIENT" | "SUPPLY_ITEM";
interface RFQLine {
  /** Kalem tipi — mutfak hammaddesi (stoklu) VEYA mutfak-dışı kalem (stoksuz). */
  kind: LineKind;
  /** Seçilen kalemin id'si (ingredient veya supply_item). */
  item_id: string;
  /** Adı/birimi — picker'dan gelir, satırda gösterilir. */
  item_name: string;
  unit: string;
  quantity: string;
}

const emptyLine = (): RFQLine => ({
  kind: "INGREDIENT", item_id: "", item_name: "", unit: "", quantity: "1",
});

function CreateRFQWizard({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const createRfq = useCreateRFQ();
  const suggest = useSuggestSuppliers();

  const [lines, setLines] = useState<RFQLine[]>([emptyLine()]);
  // Seçili tedarikçi id'leri + id→ad (picker'dan gelir, Pill gösterimi için).
  const [supplierIds, setSupplierIds] = useState<string[]>([]);
  const [supplierNames, setSupplierNames] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  // Hangi satır için picker açık — null ise kapalı. Tip satırın kind'ına göre.
  const [pickerLineIdx, setPickerLineIdx] = useState<number | null>(null);
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  // Kritik stok modalı — biten/azalan hammaddeleri toplu ekle.
  const [criticalOpen, setCriticalOpen] = useState(false);
  // Kategoriden ekle modalı — kategori seç → kalemleri otomatik doldur.
  const [categoryOpen, setCategoryOpen] = useState(false);
  // Önerilen tedarikçiler — kalem → öneri listesi (suggest-suppliers'tan).
  const [suggestions, setSuggestions] = useState<Record<string, SupplierSuggestion[]>>({});

  /** Kritik stok modalından seçilen hammaddeleri ihtiyaç listesine ekle.
   *  Zaten listede olanlar atlanır; ilk boş satır varsa o doldurulur. */
  const addCriticalItems = (
    picked: { ingredient_id: string; ingredient_name: string; unit: string; quantity: string }[],
  ) => {
    setLines((prev) => {
      // Kritik stok yalnız hammaddedir (INGREDIENT).
      const existing = new Set(
        prev.filter((l) => l.kind === "INGREDIENT").map((l) => l.item_id).filter(Boolean),
      );
      const toAdd = picked.filter((p) => !existing.has(p.ingredient_id));
      if (toAdd.length === 0) return prev;
      const base =
        prev.length === 1 && !prev[0].item_id ? [] : [...prev];
      return [
        ...base,
        ...toAdd.map((p) => ({
          kind: "INGREDIENT" as LineKind,
          item_id: p.ingredient_id,
          item_name: p.ingredient_name,
          unit: p.unit,
          quantity: p.quantity,
        })),
      ];
    });
  };

  // Kategoriden gelen kalemleri (ingredient + supply_item) ihtiyaç listesine ekle.
  // Zaten ekli olanlar (kind+id) atlanır.
  const addCategoryItems = (
    ingredients: { id: string; name: string; unit: string }[],
    supplyItems: { id: string; name: string; unit: string }[],
  ) => {
    setLines((prev) => {
      const existing = new Set(prev.map((l) => `${l.kind}:${l.item_id}`));
      const toAdd: RFQLine[] = [];
      for (const i of ingredients) {
        if (!existing.has(`INGREDIENT:${i.id}`)) {
          toAdd.push({ kind: "INGREDIENT", item_id: i.id, item_name: i.name, unit: i.unit, quantity: "1" });
        }
      }
      for (const s of supplyItems) {
        if (!existing.has(`SUPPLY_ITEM:${s.id}`)) {
          toAdd.push({ kind: "SUPPLY_ITEM", item_id: s.id, item_name: s.name, unit: s.unit, quantity: "1" });
        }
      }
      if (toAdd.length === 0) return prev;
      const base = prev.length === 1 && !prev[0].item_id ? [] : [...prev];
      return [...base, ...toAdd];
    });
  };

  const addLine = () => setLines([...lines, emptyLine()]);
  const removeLine = (idx: number) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx: number, patch: Partial<RFQLine>) => {
    setLines(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  // Tip değişince seçili kalemi temizle (hammadde↔mutfak-dışı tutarlılığı).
  const changeLineKind = (idx: number, kind: LineKind) => {
    setLines(lines.map((l, i) =>
      i === idx ? { ...l, kind, item_id: "", item_name: "", unit: "" } : l,
    ));
  };

  // item_ref'i backend formatına çevir (kind→FK alanı). XOR: tek alan dolu.
  const lineToItemRef = (l: RFQLine) =>
    l.kind === "SUPPLY_ITEM" ? { supply_item: l.item_id } : { ingredient: l.item_id };

  // Seçili kalemler için tedarikçi önerisi çek (kalem ekleyince / tip değişince).
  const fetchSuggestions = async () => {
    const valid = lines.filter((l) => l.item_id);
    if (valid.length === 0) {
      setSuggestions({});
      return;
    }
    try {
      const res = await suggest.mutateAsync(valid.map(lineToItemRef));
      setSuggestions(res.suggestions || {});
    } catch {
      /* öneri başarısızsa sessizce geç — manuel seçim hâlâ mümkün */
    }
  };

  // Kalemler değiştikçe önerileri tazele (debounce: basit, kalem id listesi değişince).
  const lineKey = lines.map((l) => `${l.kind}:${l.item_id}`).join(",");
  useEffect(() => {
    fetchSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineKey]);

  // Önerilen tedarikçiyi davet listesine ekle.
  const inviteSuggested = (s: SupplierSuggestion) => {
    if (!supplierIds.includes(s.supplier_id)) {
      setSupplierIds((prev) => [...prev, s.supplier_id]);
      setSupplierNames((prev) => ({ ...prev, [s.supplier_id]: s.supplier_name }));
    }
  };

  // Tüm benzersiz önerileri tek tıkla davet et.
  const inviteAllSuggested = () => {
    const flat = Object.values(suggestions).flat();
    const seen = new Set(supplierIds);
    const nextIds = [...supplierIds];
    const nextNames = { ...supplierNames };
    for (const s of flat) {
      if (!seen.has(s.supplier_id)) {
        seen.add(s.supplier_id);
        nextIds.push(s.supplier_id);
        nextNames[s.supplier_id] = s.supplier_name;
      }
    }
    setSupplierIds(nextIds);
    setSupplierNames(nextNames);
  };

  // Önerileri TEDARİKÇİ bazında grupla — aynı tedarikçi tekrar etmesin.
  // supplier_id → { name, is_preferred, itemNames[] (kapsadığı kalemler) }
  const supplierSuggestions = (() => {
    const byId = new Map<string, {
      supplier_id: string; supplier_name: string; is_preferred: boolean;
      source: "direct" | "category"; items: string[];
    }>();
    // Kalem id → ad eşlemesi (hangi kalemi kapsadığını yazmak için).
    const nameOf: Record<string, string> = {};
    for (const l of lines) {
      if (l.item_id) {
        const key = `${l.kind === "SUPPLY_ITEM" ? "supply_item" : "ingredient"}:${l.item_id}`;
        nameOf[key] = l.item_name;
      }
    }
    for (const [key, arr] of Object.entries(suggestions)) {
      for (const s of arr) {
        const cur = byId.get(s.supplier_id);
        if (cur) {
          if (!cur.items.includes(nameOf[key])) cur.items.push(nameOf[key]);
          if (s.is_preferred) cur.is_preferred = true;
          if (s.source === "direct") cur.source = "direct";
        } else {
          byId.set(s.supplier_id, {
            supplier_id: s.supplier_id,
            supplier_name: s.supplier_name,
            is_preferred: s.is_preferred,
            source: s.source,
            items: [nameOf[key]],
          });
        }
      }
    }
    // Tercih edilenler + çok kalem kapsayanlar üstte.
    return [...byId.values()].sort(
      (a, b) => Number(b.is_preferred) - Number(a.is_preferred) || b.items.length - a.items.length,
    );
  })();

  const handleSubmit = async () => {
    const validLines = lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (validLines.length === 0) {
      notifications.show({ message: "En az bir kalem seçin", color: "red" });
      return;
    }
    if (supplierIds.length === 0) {
      notifications.show({ message: "En az bir tedarikçi seçin", color: "red" });
      return;
    }
    try {
      const rfq = await createRfq.mutateAsync({
        lines: validLines.map((l) => ({
          ...lineToItemRef(l),
          quantity: l.quantity,
        })),
        supplier_ids: supplierIds,
        notes,
      });
      notifications.show({
        title: "RFQ oluşturuldu",
        message: `${rfq.rfq_number} — ${supplierIds.length} tedarikçiye teklif gönderilmeye hazır`,
        color: "green",
      });
      onDone();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Hata",
        color: "red",
      });
    }
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Yeni RFQ"
        subtitle="İhtiyaç listesi + tedarikçi seçimi"
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={onCancel}>
            Geri
          </Button>
        }
      />

      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">İhtiyaç Listesi</Title>
        <Stack gap="sm">
          {lines.map((line, idx) => (
            <Group key={idx} align="flex-end">
              {/* Kalem tipi: mutfak hammaddesi (stoklu) vs mutfak-dışı (stoksuz) */}
              <Stack gap={4}>
                <Text size="sm" fw={500}>Tip</Text>
                <SegmentedControl
                  size="xs"
                  value={line.kind}
                  onChange={(v) => changeLineKind(idx, v as LineKind)}
                  data={[
                    { label: "Hammadde", value: "INGREDIENT" },
                    { label: "Mutfak-dışı", value: "SUPPLY_ITEM" },
                  ]}
                />
              </Stack>
              <Stack gap={4} style={{ flex: 2 }}>
                <Text size="sm" fw={500}>
                  {line.kind === "SUPPLY_ITEM" ? "Mutfak-dışı kalem" : "Hammadde"}
                </Text>
                {/* Server-side aranan picker — kind'a göre ingredient/supply_item */}
                <Button
                  variant="default"
                  justify="space-between"
                  rightSection={<IconSearch size={16} />}
                  onClick={() => setPickerLineIdx(idx)}
                  styles={{ label: { overflow: "hidden", textOverflow: "ellipsis" } }}
                >
                  {line.item_id
                    ? `${line.item_name} (${line.unit})`
                    : line.kind === "SUPPLY_ITEM" ? "Kalem seç..." : "Hammadde seç..."}
                </Button>
              </Stack>
              <NumberInput
                label="Miktar"
                value={Number(line.quantity)}
                onChange={(v) => updateLine(idx, { quantity: String(v) })}
                min={0.001}
                decimalScale={3}
                style={{ flex: 1 }}
              />
              <ActionIcon
                color="red"
                onClick={() => removeLine(idx)}
                disabled={lines.length <= 1}
                aria-label="Sil"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group>
            <Button variant="light" leftSection={<IconPlus size={14} />} onClick={addLine}>
              Ürün Ekle
            </Button>
            <Button
              variant="light"
              color="orange"
              leftSection={<IconAlertCircle size={14} />}
              onClick={() => setCriticalOpen(true)}
            >
              Kritik Stoktan Ekle
            </Button>
            <Button
              variant="light"
              color="teal"
              leftSection={<IconCategory2 size={14} />}
              onClick={() => setCategoryOpen(true)}
            >
              Kategoriden Ekle
            </Button>
          </Group>

          <CriticalStockModal
            opened={criticalOpen}
            onClose={() => setCriticalOpen(false)}
            existingIds={lines
              .filter((l) => l.kind === "INGREDIENT")
              .map((l) => l.item_id)
              .filter(Boolean)}
            onAdd={(picked) => { addCriticalItems(picked); setCriticalOpen(false); }}
          />

          <CategoryPickModal
            opened={categoryOpen}
            onClose={() => setCategoryOpen(false)}
            onAdd={(ings, sis) => { addCategoryItems(ings, sis); setCategoryOpen(false); }}
          />

          {/* Hammadde picker — yalnız aktif satır INGREDIENT ise açık */}
          <IngredientPickerModal
            opened={pickerLineIdx !== null && lines[pickerLineIdx]?.kind === "INGREDIENT"}
            onClose={() => setPickerLineIdx(null)}
            onSelect={(ing) => {
              if (pickerLineIdx !== null) {
                updateLine(pickerLineIdx, {
                  item_id: ing.id, item_name: ing.name, unit: ing.unit,
                });
              }
            }}
            excludeIds={lines
              .filter((l, i) => i !== pickerLineIdx && l.kind === "INGREDIENT")
              .map((l) => l.item_id)
              .filter(Boolean)}
          />

          {/* Mutfak-dışı kalem picker — yalnız aktif satır SUPPLY_ITEM ise açık */}
          <SupplyItemPickerModal
            opened={pickerLineIdx !== null && lines[pickerLineIdx]?.kind === "SUPPLY_ITEM"}
            onClose={() => setPickerLineIdx(null)}
            onSelect={(si) => {
              if (pickerLineIdx !== null) {
                updateLine(pickerLineIdx, {
                  item_id: si.id, item_name: si.name, unit: si.unit,
                });
              }
            }}
            excludeIds={lines
              .filter((l, i) => i !== pickerLineIdx && l.kind === "SUPPLY_ITEM")
              .map((l) => l.item_id)
              .filter(Boolean)}
          />
        </Stack>
      </Paper>

      {/* ÖNERİLEN TEDARİKÇİLER — "hangi malı kimden alıyoruz" otomatik gelir.
          Kalem seçilince suggest-suppliers'tan (SupplierProduct + kategori). */}
      {Object.values(suggestions).some((arr) => arr.length > 0) && (
        <Paper p="lg" radius="md" withBorder bg="var(--mantine-color-blue-0)">
          <Group justify="space-between" mb="sm">
            <Group gap={6}>
              <IconBulb size={18} />
              <Title order={5}>Önerilen Tedarikçiler</Title>
              {suggest.isPending && <Loader size="xs" />}
            </Group>
            <Button size="compact-xs" variant="light" onClick={inviteAllSuggested}>
              Tümünü davet et
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mb="sm">
            Seçtiğiniz kalemleri sağlayan tedarikçiler — her biri kaç kaleminizi
            kapsıyor görünüyor. Tıklayıp davet listesine ekleyin.
          </Text>
          <Stack gap={6}>
            {supplierSuggestions.map((s) => {
              const invited = supplierIds.includes(s.supplier_id);
              return (
                <Card key={s.supplier_id} withBorder radius="md" padding="xs"
                  {...(invited ? { bg: "var(--mantine-color-green-0)" } : {})}>
                  <Group justify="space-between" wrap="nowrap">
                    <div style={{ minWidth: 0 }}>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm" fw={600} truncate>{s.supplier_name}</Text>
                        {s.is_preferred && (
                          <Badge size="xs" color="yellow" variant="light"
                            leftSection={<IconStar size={10} />}>tercih</Badge>
                        )}
                        <Badge size="xs" variant="light" color="blue">
                          {s.items.length} kalem
                        </Badge>
                        {s.source === "category" && (
                          <Badge size="xs" variant="outline" color="gray">kategori</Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" truncate>{s.items.join(", ")}</Text>
                    </div>
                    <Button
                      size="compact-xs"
                      variant={invited ? "filled" : "light"}
                      color={invited ? "green" : "blue"}
                      leftSection={invited ? <IconCheck size={13} /> : <IconPlus size={13} />}
                      onClick={() => inviteSuggested({
                        supplier_id: s.supplier_id, supplier_name: s.supplier_name,
                        is_preferred: s.is_preferred, source: s.source, last_price_minor: null,
                      })}
                      disabled={invited}
                      style={{ flexShrink: 0 }}
                    >
                      {invited ? "Davet edildi" : "Davet et"}
                    </Button>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </Paper>
      )}

      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          Tedarikçiler ({supplierIds.length} seçildi)
        </Title>
        {supplierIds.length > 0 ? (
          <Group gap={6} mb="sm">
            {supplierIds.map((id) => (
              <Pill
                key={id}
                withRemoveButton
                onRemove={() => setSupplierIds(supplierIds.filter((x) => x !== id))}
              >
                {supplierNames[id] || "Tedarikçi"}
              </Pill>
            ))}
          </Group>
        ) : (
          <Text size="xs" c="dimmed" mb="sm">Henüz tedarikçi seçilmedi</Text>
        )}
        <Button
          variant="light"
          size="compact-sm"
          leftSection={<IconPlus size={14} />}
          onClick={() => setSupplierPickerOpen(true)}
        >
          Tedarikçi Ekle
        </Button>
        <Text size="xs" c="dimmed" mt="xs">
          Her tedarikçi için ayrı PENDING teklif satırı oluşur. Tedarikçiler
          fiyat verince burada karşılaştırma yapacaksınız.
        </Text>

        <SupplierPickerModal
          opened={supplierPickerOpen}
          onClose={() => setSupplierPickerOpen(false)}
          onSelect={(s) => {
            if (!supplierIds.includes(s.id)) {
              setSupplierIds([...supplierIds, s.id]);
              setSupplierNames((prev) => ({ ...prev, [s.id]: s.name }));
            }
          }}
          excludeIds={supplierIds}
          // RFQ kalemlerini kapsayan tedarikçilere filtrele (sadece et varsa
          // Sebze çıkmasın). Öneri yoksa (kalem seçilmemiş/eşleşme yok) filtre
          // uygulanmaz → tüm tedarikçiler (geri düşüş).
          {...(supplierSuggestions.length > 0
            ? { allowIds: supplierSuggestions.map((s) => s.supplier_id) }
            : {})}
        />
      </Paper>

      <TextInput
        label="Not (opsiyonel)"
        placeholder="Haftalık alım, acil teslimat..."
        value={notes}
        onChange={(e) => setNotes(e.currentTarget.value)}
      />

      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>İptal</Button>
        <Button onClick={handleSubmit} loading={createRfq.isPending}>
          RFQ Oluştur (Taslak)
        </Button>
      </Group>
    </Stack>
  );
}

// ============================================================
// KRİTİK STOK MODALI — biten/azalan hammaddeleri toplu seç → ihtiyaç listesi
// ============================================================
function CriticalStockModal({
  opened, onClose, existingIds, onAdd,
}: {
  opened: boolean;
  onClose: () => void;
  existingIds: string[];
  onAdd: (picked: { ingredient_id: string; ingredient_name: string; unit: string; quantity: string }[]) => void;
}) {
  // only_low=true → sadece eşik altı / hızlı tükenen hammaddeler.
  const { data, isLoading } = useReorderSuggestions(14, true);
  const items: any[] = data?.items || [];
  const existing = new Set(existingIds);

  // Seçili ingredient_id → eklenecek miktar (varsayılan: önerilen miktar).
  const [picked, setPicked] = useState<Record<string, string>>({});

  // Modal açıldığında listedeki tüm kritik ürünler varsayılan SEÇİLİ gelsin
  // ("hepsini RFQ'ya koy" en yaygın senaryo). Zaten listede olanlar hariç.
  useEffect(() => {
    if (!opened) return;
    const init: Record<string, string> = {};
    for (const it of items) {
      if (existing.has(it.ingredient_id)) continue;
      // suggested_quantity 0 ise (eşik altı ama tüketim yok) min 1 öner.
      const q = Number(it.suggested_quantity) > 0 ? it.suggested_quantity : "1";
      init[it.ingredient_id] = String(q);
    }
    setPicked(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, data]);

  const toggle = (it: any) => {
    setPicked((p) => {
      const next = { ...p };
      if (next[it.ingredient_id] !== undefined) {
        delete next[it.ingredient_id];
      } else {
        const q = Number(it.suggested_quantity) > 0 ? it.suggested_quantity : "1";
        next[it.ingredient_id] = String(q);
      }
      return next;
    });
  };

  const selectableItems = items.filter((it) => !existing.has(it.ingredient_id));
  const selectedCount = Object.keys(picked).length;

  const handleAdd = () => {
    const result = selectableItems
      .filter((it) => picked[it.ingredient_id] !== undefined)
      .map((it) => ({
        ingredient_id: it.ingredient_id,
        ingredient_name: it.ingredient_name,
        unit: it.unit,
        quantity: picked[it.ingredient_id] || "1",
      }));
    onAdd(result);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="⚠️ Kritik Stoktan Ekle"
      size="lg"
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Eşik altına düşen veya hızla tükenen hammaddeler. Seçtiklerinizi
          önerilen miktarlarla ihtiyaç listesine ekleyin (miktarı
          değiştirebilirsiniz).
        </Text>
        {isLoading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : selectableItems.length === 0 ? (
          <Alert color="green" icon={<IconCheck />}>
            {items.length === 0
              ? "Kritik stokta hammadde yok — her şey yeterli görünüyor."
              : "Kritik ürünlerin tümü zaten ihtiyaç listesinde."}
          </Alert>
        ) : (
          <Table.ScrollContainer minWidth={520}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 40 }}></Table.Th>
                  <Table.Th>Hammadde</Table.Th>
                  <Table.Th ta="right">Mevcut</Table.Th>
                  <Table.Th ta="right" style={{ width: 130 }}>Eklenecek Miktar</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {selectableItems.map((it) => {
                  const checked = picked[it.ingredient_id] !== undefined;
                  return (
                    <Table.Tr key={it.ingredient_id}>
                      <Table.Td>
                        <Switch
                          checked={checked}
                          onChange={() => toggle(it)}
                          aria-label={`${it.ingredient_name} seç`}
                        />
                      </Table.Td>
                      <Table.Td fw={500}>
                        {it.ingredient_name}
                        <Text span size="xs" c="dimmed"> ({it.unit})</Text>
                      </Table.Td>
                      <Table.Td ta="right" c={Number(it.current_stock) <= 0 ? "red" : "dimmed"}>
                        {it.current_stock} {it.unit}
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          value={Number(picked[it.ingredient_id] ?? it.suggested_quantity)}
                          onChange={(v) =>
                            setPicked((p) => ({ ...p, [it.ingredient_id]: String(v) }))
                          }
                          min={0.001}
                          decimalScale={3}
                          size="xs"
                          disabled={!checked}
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button
            color="orange"
            disabled={selectedCount === 0}
            onClick={handleAdd}
          >
            {selectedCount} Ürünü Ekle
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================
// KATEGORİDEN EKLE — kategori seç → kalemleri (ingredient+supply_item) otomatik ekle
// ============================================================
interface PickItem { id: string; name: string; unit: string; kind: "INGREDIENT" | "SUPPLY_ITEM" }

function CategoryPickModal({
  opened, onClose, onAdd,
}: {
  opened: boolean;
  onClose: () => void;
  onAdd: (
    ingredients: { id: string; name: string; unit: string }[],
    supplyItems: { id: string; name: string; unit: string }[],
  ) => void;
}) {
  // Yalnız aktif kategoriler (soft-delete edilenler gizli).
  const { data: catData } = useResource<any>("procurement-categories", {
    filters: { is_active: true },
  });
  const categories: any[] = catData?.results || catData || [];

  const fetchItems = useCategoryItems();
  const [selected, setSelected] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // Kategorinin TÜM kalemleri (hammadde + mutfak-dışı birlikte) — tek çağrı.
  const [all, setAll] = useState<PickItem[]>([]);
  // Seçili kalemler (hepsi seçili başlar).
  const [picked, setPicked] = useState<Set<string>>(new Set());

  async function handleSelect(catId: string | null) {
    setSelected(catId);
    setSearch("");
    setAll([]);
    setPicked(new Set());
    if (!catId) return;
    const res = await fetchItems.mutateAsync(catId);
    const merged: PickItem[] = [
      ...res.ingredients.map((i) => ({ id: i.id, name: i.name, unit: i.unit, kind: "INGREDIENT" as const })),
      ...res.supply_items.map((s) => ({ id: s.id, name: s.name, unit: s.unit, kind: "SUPPLY_ITEM" as const })),
    ];
    setAll(merged);
    setPicked(new Set(merged.map((m) => m.id))); // varsayılan: hepsi seçili
  }

  function reset() { setSelected(null); setSearch(""); setAll([]); setPicked(new Set()); }
  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // Client-side arama (kategori başına az kalem; tek çağrı yeterli).
  const q = search.trim().toLocaleLowerCase("tr");
  const filtered = q ? all.filter((it) => it.name.toLocaleLowerCase("tr").includes(q)) : all;
  const allFilteredChecked = filtered.length > 0 && filtered.every((it) => picked.has(it.id));

  function handleAdd() {
    const sel = all.filter((it) => picked.has(it.id));
    const ings = sel.filter((x) => x.kind === "INGREDIENT").map(({ id, name, unit }) => ({ id, name, unit }));
    const sis = sel.filter((x) => x.kind === "SUPPLY_ITEM").map(({ id, name, unit }) => ({ id, name, unit }));
    onAdd(ings, sis);
    reset();
  }

  return (
    <Modal
      opened={opened}
      onClose={() => { reset(); onClose(); }}
      title="Kategoriden Ekle"
      size="md"
    >
      <Stack gap="sm">
        <Text size="xs" c="dimmed">
          Kategori seçin — içindeki tüm kalemler (hammadde + mutfak-dışı) gelir.
          İşaretleyip ekleyin; miktarları sonra düzenlersiniz.
        </Text>
        <Select
          label="Kategori"
          placeholder="Kasap, Hal, Temizlik..."
          data={categories.map((c) => ({ value: c.id, label: c.name }))}
          value={selected}
          onChange={handleSelect}
          searchable
        />

        {fetchItems.isPending ? (
          <Group justify="center" py="md"><Loader size="sm" /></Group>
        ) : !selected ? (
          <Text c="dimmed" size="sm" ta="center" py="md">Önce kategori seçin</Text>
        ) : all.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="md">
            Bu kategoride kalem yok — "Tedarik Kategorileri" ekranından kalem atayın.
          </Text>
        ) : (
          <>
            {all.length > 8 && (
              <TextInput
                placeholder="Kalem ara..."
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            )}
            <Group justify="space-between">
              <Text size="sm" fw={600} c="green">{picked.size}/{all.length} seçili</Text>
              <Button
                size="compact-xs" variant="subtle"
                onClick={() => setPicked((prev) => {
                  const next = new Set(prev);
                  if (allFilteredChecked) filtered.forEach((it) => next.delete(it.id));
                  else filtered.forEach((it) => next.add(it.id));
                  return next;
                })}
              >
                {allFilteredChecked ? "Görünenleri kaldır" : "Görünenleri seç"}
              </Button>
            </Group>
            <ScrollArea.Autosize mah={320} offsetScrollbars>
              {filtered.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="md">Eşleşen kalem yok</Text>
              ) : (
                <Stack gap={4}>
                  {filtered.map((it) => (
                    <Checkbox
                      key={it.id}
                      checked={picked.has(it.id)}
                      onChange={() => toggle(it.id)}
                      label={
                        <Group gap={6}>
                          <Text size="sm">{it.name}</Text>
                          <Badge size="xs" variant="light"
                            color={it.kind === "INGREDIENT" ? "blue" : "teal"}>
                            {it.kind === "INGREDIENT" ? "Hammadde" : "Mutfak-dışı"} · {it.unit}
                          </Badge>
                        </Group>
                      }
                    />
                  ))}
                </Stack>
              )}
            </ScrollArea.Autosize>
          </>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => { reset(); onClose(); }}>İptal</Button>
          <Button disabled={picked.size === 0} onClick={handleAdd}>
            {picked.size > 0 ? `${picked.size} Kalemi Ekle` : "Ekle"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================
// DETAIL — Karşılaştırma + Fiyat Girişi + Kabul
// ============================================================
function RFQDetail({ rfqId, onBack }: { rfqId: string; onBack: () => void }) {
  const { data: rfq, isLoading } = useRFQ(rfqId);
  const { data: comparison } = useRFQComparison(rfqId);
  const sendRfq = useSendRFQ();
  const cancelRfq = useCancelRFQ();
  const acceptQuote = useAcceptQuote();
  const acceptSplit = useAcceptSplit();

  const [priceModalResponseId, setPriceModalResponseId] = useState<string | null>(null);
  const [priceModalOpened, { open: openPriceModal, close: closePriceModal }] = useDisclosure(false);
  // Oluşan PO'ya tıklayınca yan drawer'da göster (sayfaya gitmeden).
  const [poDetail, setPoDetail] = useState<{ id: string } | null>(null);

  if (isLoading || !rfq) return <Loader />;

  const handleSend = async () => {
    try {
      await sendRfq.mutateAsync(rfqId);
      notifications.show({ message: "RFQ gönderildi", color: "blue" });
      // Gönder sonrası: her tedarikçi için WhatsApp paylaşımını aç (tedarikçinin
      // kayıtlı numarasıyla wa.me + PDF indirme linki dolu mesaj). Tarayıcı
      // popup engelini aşmak için kullanıcı jestinin hemen ardından (await
      // status geçişi tamamlandıktan sonra) sırayla açılır; engellenenler için
      // tedarikçi kartındaki yeşil WhatsApp düğmesi yedek kalır.
      const suppliers: Array<{ id: string; name: string }> = (rfq.responses || [])
        .map((r: any) => ({ id: r.supplier, name: r.supplier_name }))
        .filter((s: any) => s.id);
      if (suppliers.length > 0) {
        await sendRfqWhatsAppToAll(rfqId, suppliers);
      }
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const handleCancel = async () => {
    if (!(await confirmAction({
      title: "RFQ'yu iptal et",
      message: "RFQ'yu iptal etmek istediğinize emin misiniz? Gelen teklifler de iptal edilecek.",
      confirmLabel: "İptal Et",
      danger: true,
    }))) return;
    try {
      await cancelRfq.mutateAsync({ id: rfqId, reason: "Yönetici iptali" });
      notifications.show({ message: "RFQ iptal edildi", color: "gray" });
      onBack();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const handleAccept = async (responseId: string, supplierName: string) => {
    if (!(await confirmAction({
      title: "Teklifi kabul et",
      message: `${supplierName} teklifini kabul edip PO oluşturmak istediğinize emin misiniz?`,
      confirmLabel: "Kabul Et & PO Oluştur",
    }))) return;
    try {
      const po = await acceptQuote.mutateAsync(responseId);
      notifications.show({
        title: "🏆 Teklif Kabul Edildi",
        message: `${po.po_number} oluşturuldu — ${po.supplier_name}'dan ${(po.total_minor / 100).toFixed(2)}₺`,
        color: "green",
        icon: <IconTrophy />,
      });
      onBack();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const handleAcceptSplit = async (lineAwards: Record<string, string>) => {
    const supplierCount = new Set(Object.values(lineAwards)).size;
    if (!(await confirmAction({
      title: "Kalem bazında böl & PO oluştur",
      message: `Seçilen ürünler ${supplierCount} tedarikçiye bölünecek ve ${supplierCount} ayrı sipariş (PO) oluşturulacak. Onaylıyor musunuz?`,
      confirmLabel: "Böl & PO'ları Oluştur",
    }))) return;
    try {
      const res = await acceptSplit.mutateAsync({ rfqId, lineAwards });
      notifications.show({
        title: "🏆 RFQ Bölünerek Kabul Edildi",
        message: `${res.po_count} sipariş oluşturuldu: ${res.pos.map((p: any) => p.po_number).join(", ")}`,
        color: "green",
        icon: <IconTrophy />,
      });
      onBack();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const openPriceForm = (responseId: string) => {
    setPriceModalResponseId(responseId);
    openPriceModal();
  };

  const isAccepted = rfq.status === "ACCEPTED";
  const isDraft = rfq.status === "DRAFT";
  // Kabul sonrası: hangi tedarikçi(ler) kazandı (parçalı/split kabulde birden
  // çok). Karşılaştırma kartında kazananları yeşil "KABUL EDİLDİ" ile işaretle.
  const acceptedSupplierIds = new Set<string>(
    (rfq.resulting_pos || []).map((po: any) => po.supplier_id).filter(Boolean),
  );
  // Parçalı kabulde: tedarikçi → o tedarikçinin PO'sunda alınan kalem id'leri.
  // Karşılaştırma satırında "bu kalem bu tedarikçiden alındı" yeşil işareti için.
  const wonItemsBySupplier: Record<string, Set<string>> = {};
  for (const po of (rfq.resulting_pos || [])) {
    if (!po.supplier_id) continue;
    wonItemsBySupplier[po.supplier_id] = new Set<string>(po.item_ids || []);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={`RFQ ${rfq.rfq_number}`}
        subtitle={`${rfq.lines.length} ürün × ${rfq.responses.length} tedarikçi`}
        actions={
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
              Geri
            </Button>
            <Button
              variant="light"
              leftSection={<IconFileDownload size={16} />}
              onClick={() => downloadRfqPdf(rfqId, rfq.rfq_number)}
            >
              PDF İndir
            </Button>
            {isDraft && (
              <Button leftSection={<IconSend size={16} />} onClick={handleSend} loading={sendRfq.isPending}>
                Tedarikçilere Gönder
              </Button>
            )}
            {!isAccepted && rfq.status !== "CANCELLED" && (
              <Button color="red" variant="light" leftSection={<IconX size={16} />} onClick={handleCancel}>
                İptal
              </Button>
            )}
          </Group>
        }
      />

      <Group>
        <Badge size="lg" color={STATUS_COLOR[rfq.status]}>
          {STATUS_LABEL[rfq.status] || rfq.status}
        </Badge>
        {rfq.notes && <Text size="sm" c="dimmed">"{rfq.notes}"</Text>}
      </Group>

      {isAccepted && (rfq.resulting_po || (rfq.resulting_pos?.length ?? 0) > 0) && (
        <Alert color="green" icon={<IconTrophy />} title="RFQ Kabul Edildi">
          {(rfq.resulting_pos?.length ?? 0) > 1 ? (
            <Stack gap={4}>
              <Text>
                Bu RFQ kalem bazında bölündü — {rfq.resulting_pos.length} ayrı
                sipariş (PO) oluşturuldu:
              </Text>
              {rfq.resulting_pos.map((po: any) => (
                <Stack key={po.id} gap={2}>
                  <Group gap={6}>
                    <Text size="sm">•</Text>
                    {/* PO'ya tıkla → yan drawer (sayfaya gitmeden). */}
                    <Button
                      variant="subtle" size="compact-sm" p={4}
                      onClick={() => setPoDetail({ id: po.id })}
                    >
                      {po.po_number}
                    </Button>
                    <Text size="sm">— {po.supplier_name} (<MoneyDisplay minor={po.total_minor} />)</Text>
                  </Group>
                  {/* Bu PO hangi kalemleri içeriyor (parçalı kabulde net görünür). */}
                  {(po.items || []).map((it: any, ix: number) => (
                    <Text key={ix} size="xs" c="dimmed" pl={28}>
                      {it.name} — {it.quantity}{it.unit ? ` ${it.unit}` : ""} ×{" "}
                      <MoneyDisplay minor={it.unit_price_minor} /> ={" "}
                      <Text span fw={600}><MoneyDisplay minor={it.line_total_minor} /></Text>
                    </Text>
                  ))}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Group gap={6}>
              <Text size="sm">Bu RFQ'dan PO oluşturuldu:</Text>
              {rfq.resulting_pos?.[0] && (
                <Button
                  variant="subtle" size="compact-sm" p={4}
                  onClick={() => setPoDetail({ id: rfq.resulting_pos[0].id })}
                >
                  {rfq.resulting_pos[0].po_number}
                </Button>
              )}
            </Group>
          )}
        </Alert>
      )}

      {/* İhtiyaç Listesi */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">İhtiyaç Listesi</Title>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ürün</Table.Th>
              <Table.Th ta="right">Miktar</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rfq.lines.map((line: any) => (
              <Table.Tr key={line.id}>
                <Table.Td fw={500}>{lineName(line)}</Table.Td>
                <Table.Td ta="right">
                  {line.quantity_needed} {lineUnit(line)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Karşılaştırma */}
      {comparison?.comparison && comparison.comparison.length > 0 && (
        <Paper p="lg" radius="md" withBorder>
          <Title order={4} mb="md">Teklif Karşılaştırması</Title>
          <Stack gap="sm">
            {comparison.comparison.map((q: any) => {
              // Kabul sonrası bu tedarikçi kazandı mı (parçalı dahil)?
              const isWinner = isAccepted && acceptedSupplierIds.has(q.supplier_id);
              // Kabul edilmeden önce: en ucuzu vurgula. Sonra: kazananı vurgula.
              const highlight = isAccepted ? isWinner : q.is_cheapest;
              return (
              <Card
                key={q.response_id}
                padding="md"
                radius="md"
                withBorder
                style={{
                  borderColor: highlight ? "#40c057" : undefined,
                  borderWidth: highlight ? 2 : 1,
                }}
              >
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    {isWinner && (
                      <Badge color="green" leftSection={<IconCheck size={12} />}>
                        KABUL EDİLDİ
                      </Badge>
                    )}
                    {!isAccepted && q.is_cheapest && (
                      <Badge color="green" leftSection={<IconTrophy size={12} />}>
                        EN UCUZ
                      </Badge>
                    )}
                    <Text fw={700} size="lg">{q.supplier_name}</Text>
                    <Badge color={q.status === "RECEIVED" ? "blue" : "gray"} variant="light">
                      {q.status === "RECEIVED" ? "Fiyat geldi" :
                       q.status === "PENDING" ? "Bekleniyor" : q.status}
                    </Badge>
                    <Tooltip label={`${q.supplier_name} için PDF (sadece kapsadığı kalemler)`}>
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => downloadRfqPdf(rfqId, rfq.rfq_number, q.supplier_id, q.supplier_name)}
                        aria-label="Tedarikçi PDF"
                      >
                        <IconFileDownload size={18} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="WhatsApp ile RFQ formunu gönder">
                      <ActionIcon
                        variant="subtle"
                        color="green"
                        onClick={() => shareRfqWhatsApp(rfqId, q.supplier_id)}
                        aria-label="WhatsApp gönder"
                      >
                        <IconBrandWhatsapp size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  <Group gap="xs">
                    {q.status === "RECEIVED" ? (
                      <>
                        <Text fw={700} size="xl" {...(q.is_cheapest ? { c: "green" } : {})}>
                          <MoneyDisplay minor={q.total_minor} />
                        </Text>
                        {!isAccepted && (
                          <Button
                            color="green"
                            leftSection={<IconCheck size={16} />}
                            onClick={() => handleAccept(q.response_id, q.supplier_name)}
                            loading={acceptQuote.isPending}
                          >
                            Kabul Et → PO Oluştur
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        variant="light"
                        onClick={() => openPriceForm(q.response_id)}
                        disabled={isAccepted}
                      >
                        Fiyat Gir
                      </Button>
                    )}
                  </Group>
                </Group>
                {q.lines.length > 0 && (
                  <>
                    <Divider my="xs" />
                    <Table fz="sm">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Ürün</Table.Th>
                          <Table.Th ta="right">Miktar</Table.Th>
                          <Table.Th ta="right">Birim Fiyat</Table.Th>
                          <Table.Th ta="right">Tutar</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {q.lines.map((line: any, i: number) => {
                          // Bu kalem bu tedarikçiden mi ALINDI (parçalı kabul)?
                          const itemId = line.ingredient_id || line.supply_item_id || "";
                          const wonHere = isAccepted && itemId
                            && wonItemsBySupplier[q.supplier_id]?.has(itemId);
                          return (
                          <Table.Tr
                            key={i}
                            {...(wonHere ? { bg: "var(--mantine-color-green-0)" } : {})}
                          >
                            <Table.Td>
                              <Group gap={6} wrap="nowrap">
                                {wonHere && <IconCheck size={14} color="var(--mantine-color-green-7)" />}
                                <Text size="sm" fw={wonHere ? 600 : 400}
                                  {...(isAccepted && !wonHere ? { c: "dimmed" } : {})}>
                                  {lineName(line)}
                                </Text>
                                {wonHere && (
                                  <Badge size="xs" color="green" variant="light">Alındı</Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td ta="right" c="dimmed">
                              {line.quantity}{lineUnit(line) ? ` ${lineUnit(line)}` : ""}
                            </Table.Td>
                            <Table.Td ta="right" {...(isAccepted && !wonHere ? { c: "dimmed" } : {})}>
                              {line.available ? (
                                <MoneyDisplay minor={line.unit_price_minor} />
                              ) : (
                                <Badge color="red" variant="light">Mevcut Değil</Badge>
                              )}
                            </Table.Td>
                            <Table.Td ta="right" fw={wonHere ? 700 : 500}
                              {...(wonHere ? { c: "green" } : (isAccepted && !wonHere ? { c: "dimmed" } : {}))}>
                              {line.available && (
                                <MoneyDisplay minor={line.line_total_minor} />
                              )}
                            </Table.Td>
                          </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                    {q.lead_time_days > 0 && (
                      <Text size="xs" c="dimmed" mt="xs">
                        Teslimat süresi: {q.lead_time_days} gün
                      </Text>
                    )}
                  </>
                )}
              </Card>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Kalem bazında böl — domates A'dan, biber B'den; çoklu PO.
          Yalnız BİRDEN FAZLA tedarikçi fiyat verdiyse anlamlı (kalemleri farklı
          firmalara dağıtmak için). Tek tedarikçi teklif verdiyse böl zaten tek
          PO üretir → üstteki "Kabul Et → PO" yeterli, böl bölümünü gizle. */}
      {!isAccepted && rfq.status !== "CANCELLED" &&
        (comparison?.comparison || []).filter((q: any) => q.status === "RECEIVED").length > 1 && (
        <LineSplitAward
          rfq={rfq}
          lineBest={comparison?.line_best || {}}
          comparison={comparison?.comparison || []}
          onAward={handleAcceptSplit}
          submitting={acceptSplit.isPending}
        />
      )}

      <PriceEntryModal
        opened={priceModalOpened}
        responseId={priceModalResponseId}
        rfq={rfq}
        onClose={() => { closePriceModal(); setPriceModalResponseId(null); }}
      />
      <PurchaseOrderDetailDrawer po={poDetail} onClose={() => setPoDetail(null)} />
    </Stack>
  );
}

// ============================================================
// KALEM BAZINDA BÖL — her ürünü en ucuz/farklı tedarikçiden seç → çoklu PO
// ============================================================
function LineSplitAward({
  rfq, lineBest, comparison, onAward, submitting,
}: {
  rfq: any;
  /** {request_line_id: {response_id, supplier_id, supplier_name, unit_price_minor}} */
  lineBest: Record<string, any>;
  comparison: any[];
  onAward: (lineAwards: Record<string, string>) => void;
  submitting: boolean;
}) {
  // Her ihtiyaç satırı için seçili response_id. Başlangıç: backend'in
  // hesapladığı en ucuz available teklif (line_best).
  const [awards, setAwards] = useState<Record<string, string>>({});

  // line_best gelince (veya değişince) varsayılanı en ucuza ayarla — kullanıcı
  // henüz elle değiştirmediği satırlar için.
  useEffect(() => {
    setAwards((prev) => {
      const next = { ...prev };
      for (const line of rfq.lines) {
        if (!next[line.id] && lineBest[line.id]?.response_id) {
          next[line.id] = lineBest[line.id].response_id;
        }
      }
      return next;
    });
  }, [lineBest, rfq.lines]);

  // response_id → { supplier_name, lines: {request_line_id: {available, unit_price_minor}} }
  const responseMap = useMemo(() => {
    const m: Record<string, any> = {};
    for (const q of comparison) {
      if (q.status !== "RECEIVED") continue;
      const byLine: Record<string, any> = {};
      for (const l of q.lines || []) {
        // comparison line'da request_line id yok; kalem id (ingredient VEYA
        // supply_item) ile eşleştir.
        byLine[l.ingredient_id || l.supply_item_id] = l;
      }
      m[q.response_id] = { supplier_name: q.supplier_name, byLine };
    }
    return m;
  }, [comparison]);

  // Her satır için seçilebilir tedarikçi seçenekleri (sadece o ürünü
  // available veren RECEIVED teklifler).
  const optionsForLine = (line: any) => {
    const opts: { value: string; label: string }[] = [];
    for (const q of comparison) {
      if (q.status !== "RECEIVED") continue;
      const ql = (q.lines || []).find(
        (l: any) => (l.ingredient_id || l.supply_item_id) === lineItemId(line),
      );
      if (ql && ql.available) {
        opts.push({
          value: q.response_id,
          label: `${q.supplier_name} — ${(ql.unit_price_minor / 100).toFixed(2)}₺`,
        });
      }
    }
    return opts;
  };

  // En az bir RECEIVED teklif yoksa bölme anlamsız.
  const hasReceived = comparison.some((q) => q.status === "RECEIVED");
  if (!hasReceived) return null;

  // Tüm satırlar atanmış mı + kaç farklı tedarikçi.
  const assignedLines = rfq.lines.filter((l: any) => awards[l.id]);
  const supplierCount = new Set(
    assignedLines.map((l: any) => awards[l.id]),
  ).size;
  const lineTotal = (line: any, respId: string) => {
    const rl = responseMap[respId]?.byLine?.[lineItemId(line)];
    if (!rl || !rl.available) return 0;
    return Number(line.quantity_needed) * rl.unit_price_minor;
  };
  const grandTotal = assignedLines.reduce(
    (acc: number, l: any) => acc + lineTotal(l, awards[l.id]),
    0,
  );

  const handleAutoCheapest = () => {
    const next: Record<string, string> = {};
    for (const line of rfq.lines) {
      if (lineBest[line.id]?.response_id) {
        next[line.id] = lineBest[line.id].response_id;
      }
    }
    setAwards(next);
  };

  return (
    <Paper p="lg" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Title order={4}>
          <Group gap={6}><IconArrowsSplit size={20} /> Kalem Bazında Böl</Group>
        </Title>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconTrophy size={14} />}
          onClick={handleAutoCheapest}
        >
          Tümünü en ucuza ayarla
        </Button>
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Her ürünü ayrı tedarikçiden alabilirsiniz (ör. domates A'dan, biber
        B'den). Seçimler {supplierCount} tedarikçiye bölünür ve tek RFQ'dan{" "}
        {supplierCount} ayrı sipariş (PO) oluşur.
      </Text>

      <Table.ScrollContainer minWidth={560}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ürün</Table.Th>
              <Table.Th ta="right">Miktar</Table.Th>
              <Table.Th style={{ width: 280 }}>Tedarikçi (en ucuz seçili)</Table.Th>
              <Table.Th ta="right">Satır Tutarı</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rfq.lines.map((line: any) => {
              const opts = optionsForLine(line);
              const isCheapest =
                awards[line.id] &&
                lineBest[line.id]?.response_id === awards[line.id];
              return (
                <Table.Tr key={line.id}>
                  <Table.Td fw={500}>{lineName(line)}</Table.Td>
                  <Table.Td ta="right">
                    {line.quantity_needed} {lineUnit(line)}
                  </Table.Td>
                  <Table.Td>
                    {opts.length === 0 ? (
                      <Badge color="red" variant="light">Teklif yok</Badge>
                    ) : (
                      <Group gap={6} wrap="nowrap">
                        <Select
                          data={opts}
                          value={awards[line.id] || null}
                          onChange={(v) =>
                            setAwards((p) => ({ ...p, [line.id]: v || "" }))
                          }
                          placeholder="Tedarikçi seç"
                          size="xs"
                          style={{ flex: 1 }}
                          allowDeselect={false}
                        />
                        {isCheapest && (
                          <Badge color="green" variant="light" size="sm">
                            en ucuz
                          </Badge>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td ta="right" fw={500}>
                    {awards[line.id] ? (
                      <MoneyDisplay minor={lineTotal(line, awards[line.id])} />
                    ) : "—"}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group justify="space-between" mt="md">
        <Text size="sm" c="dimmed">
          {assignedLines.length}/{rfq.lines.length} ürün atandı ·{" "}
          {supplierCount} tedarikçi · Toplam{" "}
          <Text span fw={700}><MoneyDisplay minor={grandTotal} /></Text>
        </Text>
        <Button
          color="green"
          leftSection={<IconArrowsSplit size={16} />}
          disabled={assignedLines.length === 0}
          loading={submitting}
          onClick={() => {
            const clean: Record<string, string> = {};
            for (const l of assignedLines) clean[l.id] = awards[l.id];
            onAward(clean);
          }}
        >
          Böl & {supplierCount} PO Oluştur
        </Button>
      </Group>
    </Paper>
  );
}

// ============================================================
// PRICE ENTRY MODAL
// ============================================================
function PriceEntryModal({
  opened, responseId, rfq, onClose,
}: { opened: boolean; responseId: string | null; rfq: any; onClose: () => void }) {
  const record = useRecordQuoteResponse();
  const coverable = useCoverableLines();
  const [prices, setPrices] = useState<Record<string, { price: string; available: boolean }>>({});
  const [leadTime, setLeadTime] = useState(0);
  // Bu tedarikçinin kapsadığı request_line id'leri (null = henüz çekilmedi).
  const [coveredIds, setCoveredIds] = useState<string[] | null>(null);
  const [loadingCover, setLoadingCover] = useState(false);
  const [search, setSearch] = useState("");

  const response = rfq.responses.find((r: any) => r.id === responseId);
  const supplierId = response?.supplier;

  // Modal açılınca tedarikçinin kapsadığı kalemleri çek.
  useEffect(() => {
    if (!opened || !responseId || !supplierId) { setCoveredIds(null); return; }
    setLoadingCover(true);
    setSearch("");
    coverable.mutateAsync({ rfqId: rfq.id, supplierId })
      .then((res) => setCoveredIds(res.request_line_ids))
      .catch(() => setCoveredIds([]))
      .finally(() => setLoadingCover(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, responseId, supplierId]);

  if (!responseId) return null;
  if (!response) return null;

  // Kapsam doluysa filtrele; boş ise tüm kalemleri göster (geri düşüş).
  // coveredIds null iken (yükleniyor) flicker olmasın diye liste GÖSTERME (loader).
  const coverLines = (coveredIds && coveredIds.length > 0)
    ? rfq.lines.filter((l: any) => coveredIds.includes(l.id))
    : rfq.lines;
  // İçinde arama (çok kalemli tedarikçide bulmak için).
  const q = search.trim().toLocaleLowerCase("tr");
  const visibleLines = q
    ? coverLines.filter((l: any) => lineName(l).toLocaleLowerCase("tr").includes(q))
    : coverLines;

  const handleSave = async () => {
    const lines = visibleLines.map((l: any) => ({
      request_line: l.id,
      unit_price_minor: Math.round(Number(prices[l.id]?.price || 0) * 100),
      available: prices[l.id]?.available ?? true,
    }));
    // Tutarsız satırlar: "Var" işaretli ama fiyatı 0 (tedarikçi var dedi ama
    // fiyat girilmemiş). Bunları kaydetmeden ÖNCE otomatik "yok" işaretle,
    // kullanıcıyı uyar ve kaydı durdur — yanlışlıkla 0₺ teklif kaydı (en ucuz
    // sanılır) ve eksik fiyat girişi engellenir.
    const inconsistent = lines.filter(
      (l: any) => l.available && l.unit_price_minor <= 0,
    );
    if (inconsistent.length > 0) {
      setPrices((prev) => {
        const next = { ...prev };
        for (const l of inconsistent) {
          const cur = next[l.request_line] || { price: "0", available: true };
          next[l.request_line] = { ...cur, available: false };
        }
        return next;
      });
      const names = inconsistent
        .map((l: any) => {
          const line = visibleLines.find((vl: any) => vl.id === l.request_line);
          return line ? lineName(line) : "";
        })
        .filter(Boolean)
        .join(", ");
      notifications.show({
        title: "Fiyatı 0 olan kalemler 'yok' işaretlendi",
        message: `${names} — fiyat girilmemiş. "Var" istiyorsanız fiyat girin, sonra tekrar kaydedin.`,
        color: "orange",
      });
      return; // kaydı durdur — kullanıcı gözden geçirip tekrar Kaydet'e bassın
    }
    try {
      await record.mutateAsync({
        responseId,
        lines,
        leadTimeDays: leadTime,
      });
      notifications.show({
        title: `${response.supplier_name} fiyatları kaydedildi`,
        message: "",
        color: "green",
      });
      onClose();
      setPrices({});
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Fiyat Gir — ${response.supplier_name}`}
      size="lg"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Her ürün için tedarikçinin verdiği birim fiyatı (TL) girin.
          "Mevcut değil" ise toggle'ı kapatın.
        </Text>
        {loadingCover ? (
          <Group justify="center" py="xl"><Loader size="sm" /></Group>
        ) : (
        <>
        {coveredIds && coveredIds.length > 0 ? (
          <Text size="xs" c="teal">
            Bu tedarikçinin kapsamındaki {coverLines.length} kalem (kategori/geçmiş alım).
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            Bu tedarikçiye özel kapsam tanımlı değil — tüm kalemler gösteriliyor.
            (Tedarikçiye kategori atayarak daraltabilirsiniz.)
          </Text>
        )}
        {coverLines.length > 10 && (
          <TextInput
            placeholder="Ürün ara..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        )}
        <ScrollArea.Autosize mah={440} offsetScrollbars>
        <Table stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ürün</Table.Th>
              <Table.Th ta="right">Miktar</Table.Th>
              <Table.Th ta="right">Birim Fiyat (₺)</Table.Th>
              <Table.Th>Var mı?</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visibleLines.map((line: any) => {
              const current = prices[line.id] || { price: "0", available: true };
              return (
                <Table.Tr key={line.id}>
                  <Table.Td fw={500}>{lineName(line)}</Table.Td>
                  <Table.Td ta="right">
                    {line.quantity_needed} {lineUnit(line)}
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={Number(current.price)}
                      onChange={(v) => setPrices({
                        ...prices,
                        [line.id]: { ...current, price: String(v) },
                      })}
                      min={0}
                      decimalScale={2}
                      disabled={!current.available}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={current.available}
                      onChange={(e) => setPrices({
                        ...prices,
                        [line.id]: { ...current, available: e.currentTarget.checked },
                      })}
                    />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        </ScrollArea.Autosize>
        </>
        )}
        <NumberInput
          label="Teslimat süresi (gün)"
          value={leadTime}
          onChange={(v) => setLeadTime(Number(v))}
          min={0}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={handleSave} loading={record.isPending}>
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================
// PDF + WhatsApp helpers (Sprint 35)
// ============================================================
async function downloadRfqPdf(
  rfqId: string, rfqNumber: string,
  supplierId?: string, supplierName?: string,
) {
  try {
    const response = await apiClient.get(`/api/v1/rfqs/${rfqId}/pdf/`, {
      responseType: "blob",
      ...(supplierId ? { params: { supplier: supplierId } } : {}),
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = supplierName
      ? `RFQ-${rfqNumber}-${supplierName}.pdf`
      : `RFQ-${rfqNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    notifications.show({ message: "PDF indirildi", color: "green" });
  } catch (e: any) {
    notifications.show({
      message: e?.response?.data?.detail || "PDF üretilemedi",
      color: "red",
    });
  }
}

/**
 * "Gönder" akışında her tedarikçiye sırayla WhatsApp paylaşımı açar.
 * Her tedarikçinin kayıtlı telefonuyla wa.me linki + PDF indirme linkli mesaj
 * hazır gelir. Numarası olmayan tedarikçiler için wa.me yine açılır (kontak
 * elle seçilir) ve sonda uyarı gösterilir.
 *
 * Not: Tarayıcılar bir kullanıcı jestinden sonra çok sayıda window.open'ı
 * popup olarak engelleyebilir. İlk sekme genelde açılır; engellenenler için
 * tedarikçi kartındaki yeşil WhatsApp düğmesi tek tek yedek yoldur.
 */
async function sendRfqWhatsAppToAll(
  rfqId: string,
  suppliers: Array<{ id: string; name: string }>,
) {
  const missingPhone: string[] = [];
  let blocked = 0;
  for (const s of suppliers) {
    try {
      const res = await apiClient.get(
        `/api/v1/rfqs/${rfqId}/whatsapp-share/`,
        { params: { supplier_id: s.id } },
      );
      if (!res.data.supplier_phone) missingPhone.push(s.name || "Tedarikçi");
      const win = window.open(res.data.whatsapp_url, "_blank", "noopener,noreferrer");
      if (!win) blocked += 1;
    } catch {
      blocked += 1;
    }
  }
  if (missingPhone.length > 0) {
    notifications.show({
      title: "Telefon kayıtlı değil",
      message: `${missingPhone.join(", ")} için numara yok — WhatsApp kontağını elle seçin.`,
      color: "yellow",
    });
  }
  if (blocked > 0) {
    notifications.show({
      title: "Bazı WhatsApp sekmeleri engellendi",
      message: "Tarayıcı popup engeli olabilir. Tedarikçi kartındaki yeşil WhatsApp düğmesiyle tek tek gönderin.",
      color: "orange",
    });
  }
}

async function shareRfqWhatsApp(rfqId: string, supplierId: string) {
  try {
    const res = await apiClient.get(
      `/api/v1/rfqs/${rfqId}/whatsapp-share/`,
      { params: { supplier_id: supplierId } },
    );
    // wa.me link'i yeni sekmede aç
    window.open(res.data.whatsapp_url, "_blank", "noopener,noreferrer");
    if (!res.data.supplier_phone) {
      notifications.show({
        title: "Tedarikçi telefonu yok",
        message: "WhatsApp kontağı manuel seçin. Mesaj hazır geldi.",
        color: "yellow",
      });
    }
  } catch (e: any) {
    notifications.show({
      message: e?.response?.data?.detail || "WhatsApp linki üretilemedi",
      color: "red",
    });
  }
}
